import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../utils/api';
import { io } from 'socket.io-client';

const SessionContext = createContext();

export const useSession = () => useContext(SessionContext);

export const SessionProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [socket, setSocket] = useState(null);

  // Initialize socket connection when session is set
  useEffect(() => {
    if (session) {
      console.log('Initializing socket connection for session:', session.id);
      
      // Determine the socket URL based on environment
      let socketUrl;
      
      if (process.env.NODE_ENV === 'development') {
        // In development, connect to the backend server port (3001)
        socketUrl = 'http://localhost:3001';
      } else {
        // In production, use the same origin
        socketUrl = window.location.origin;
      }
      
      console.log('Using socket URL:', socketUrl);
      
      const newSocket = io(socketUrl, {
        path: '/socket.io/',
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
        timeout: 20000,
        transports: ['websocket', 'polling'],
        withCredentials: true
      });
      
      newSocket.on('connect', () => {
        console.log('Socket connected:', newSocket.id);
        const roomName = `session-${session.id}`;
        console.log(`Joining room: ${roomName}`);
        newSocket.emit('join-room', roomName);
      });
      
      newSocket.on('room-joined', (data) => {
        console.log(`Successfully joined room: ${data.room} with ${data.clients} client(s)`);
      });
      
      newSocket.on('reconnect', (attemptNumber) => {
        console.log(`Socket reconnected after ${attemptNumber} attempts`);
        const roomName = `session-${session.id}`;
        console.log(`Rejoining room after reconnection: ${roomName}`);
        newSocket.emit('join-room', roomName);
      });
      
      newSocket.on('reconnect_attempt', (attemptNumber) => {
        console.log(`Socket reconnection attempt #${attemptNumber}`);
      });
      
      newSocket.on('reconnect_error', (error) => {
        console.error('Socket reconnection error:', error);
      });
      
      newSocket.on('reconnect_failed', () => {
        console.error('Socket reconnection failed after all attempts');
      });
      
      newSocket.on('disconnect', (reason) => {
        console.log(`Socket disconnected: ${reason}`);
        if (reason === 'io server disconnect') {
          // The disconnection was initiated by the server, try to reconnect
          newSocket.connect();
        }
      });
      
      newSocket.on('error', (error) => {
        console.error('Socket error:', error);
      });
      
      setSocket(newSocket);
      
      return () => {
        console.log('Cleaning up socket connection');
        newSocket.disconnect();
      };
    }
  }, [session]);

  // Handle page lifecycle changes for mobile browser reconnection
  useEffect(() => {
    const reconnectSocket = () => {
      console.log('Page became active, reconnecting socket...');
      let socketUrl;
      
      if (process.env.NODE_ENV === 'development') {
        socketUrl = 'http://localhost:3001';
      } else {
        socketUrl = window.location.origin;
      }
      
      const newSocket = io(socketUrl, {
        path: '/socket.io/',
        reconnectionAttempts: 10,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 10000,
        timeout: 20000,
        transports: ['websocket', 'polling'],
        withCredentials: true
      });
      
      newSocket.on('connect', () => {
        console.log('Socket reconnected after lifecycle change:', newSocket.id);
        const roomName = `session-${session.id}`;
        console.log(`Re-joining room after lifecycle change: ${roomName}`);
        newSocket.emit('join-room', roomName);
      });
      
      newSocket.on('room-joined', (data) => {
        console.log(`Successfully joined room: ${data.room} with ${data.clients} client(s)`);
      });
      
      newSocket.on('reconnect', (attemptNumber) => {
        console.log(`Socket reconnected after ${attemptNumber} attempts`);
        const roomName = `session-${session.id}`;
        console.log(`Rejoining room after reconnection: ${roomName}`);
        newSocket.emit('join-room', roomName);
      });
      
      newSocket.on('reconnect_attempt', (attemptNumber) => {
        console.log(`Socket reconnection attempt #${attemptNumber}`);
      });
      
      newSocket.on('reconnect_error', (error) => {
        console.error('Socket reconnection error:', error);
      });
      
      newSocket.on('reconnect_failed', () => {
        console.error('Socket reconnection failed after all attempts');
      });
      
      newSocket.on('disconnect', (reason) => {
        console.log(`Socket disconnected: ${reason}`);
        if (reason === 'io server disconnect') {
          newSocket.connect();
        }
      });
      
      newSocket.on('error', (error) => {
        console.error('Socket error:', error);
      });
      
      setSocket(newSocket);
    };

    const rejoinRoom = () => {
      if (socket && socket.connected) {
        console.log('Page became active, re-joining session room...');
        const roomName = `session-${session.id}`;
        socket.emit('join-room', roomName);
      }
    };

    const handleVisibilityChange = () => {
      if (!document.hidden && socket && session) {
        if (socket.disconnected) {
          reconnectSocket();
        } else {
          rejoinRoom();
        }
      }
    };

    // Handle Page Lifecycle API states
    const handlePageLifecycleChange = (event) => {
      console.log('Page lifecycle state changed:', event.newState);
      
      switch (event.newState) {
        case 'active':
          // Page became active - reconnect if needed
          if (socket && socket.disconnected) {
            reconnectSocket();
          } else if (socket && socket.connected) {
            rejoinRoom();
          }
          break;
        case 'passive':
        case 'hidden':
          // Page is becoming inactive - no action needed
          console.log('Page becoming inactive:', event.newState);
          break;
        case 'frozen':
          // Page is frozen - socket will be disconnected
          console.log('Page frozen - socket will be disconnected');
          break;
        case 'terminated':
          // Page is terminated - socket will be disconnected
          console.log('Page terminated - socket will be disconnected');
          break;
      }
    };

    // Handle page resume from frozen state
    const handlePageResume = () => {
      console.log('Page resumed from frozen state');
      if (socket && socket.disconnected) {
        reconnectSocket();
      } else if (socket && socket.connected) {
        rejoinRoom();
      }
    };

    // Handle page show (back/forward cache)
    const handlePageShow = (event) => {
      console.log('Page show event, persisted:', event.persisted);
      if (event.persisted) {
        // Page was restored from back/forward cache
        if (socket && socket.disconnected) {
          reconnectSocket();
        } else if (socket && socket.connected) {
          rejoinRoom();
        }
      }
    };

    // Add all event listeners
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Page Lifecycle API events
    if ('onpagehide' in window) {
      document.addEventListener('pagehide', handlePageLifecycleChange);
    }
    if ('onpageshow' in window) {
      document.addEventListener('pageshow', handlePageShow);
    }
    if ('onfreeze' in document) {
      document.addEventListener('freeze', handlePageLifecycleChange);
    }
    if ('onresume' in document) {
      document.addEventListener('resume', handlePageResume);
    }

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if ('onpagehide' in window) {
        document.removeEventListener('pagehide', handlePageLifecycleChange);
      }
      if ('onpageshow' in window) {
        document.removeEventListener('pageshow', handlePageShow);
      }
      if ('onfreeze' in document) {
        document.removeEventListener('freeze', handlePageLifecycleChange);
      }
      if ('onresume' in document) {
        document.removeEventListener('resume', handlePageResume);
      }
    };
  }, [socket, session]);

  // Check for existing session on load
  useEffect(() => {
    checkExistingSession();
  }, []);

  const checkExistingSession = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('sessionToken');
      const sessionId = localStorage.getItem('sessionId');
      const sessionName = localStorage.getItem('sessionName');
      
      // If we have session data in localStorage, use it directly
      if (sessionId && sessionName) {
        console.log('Restoring session from localStorage:', { id: sessionId, name: sessionName });
        
        // Set the token in api headers if available
        if (token) {
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        }
        
        // Create a minimal session object from localStorage
        setSession({
          id: sessionId,
          name: sessionName
        });
      } else if (token) {
        // If we only have a token but no session data, clear it
        console.log('Found token but no session data, clearing token');
        localStorage.removeItem('sessionToken');
        delete api.defaults.headers.common['Authorization'];
      }
    } catch (err) {
      console.error('Error in checkExistingSession:', err);
      // Clear invalid token
      localStorage.removeItem('sessionToken');
      localStorage.removeItem('sessionId');
      localStorage.removeItem('sessionName');
      delete api.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  };

  // Create a new session
  const createSession = async (sessionData) => {
    try {
      setError(null);
      const response = await api.post('/api/sessions', sessionData);
      
      // Store the session token
      const { token, ...newSession } = response.data;
      if (token) {
        localStorage.setItem('sessionToken', token);
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }
      
      // Store session ID, name, and password in localStorage as fallback
      if (newSession && newSession.id) {
        localStorage.setItem('sessionId', newSession.id);
        localStorage.setItem('sessionName', newSession.name || 'Live Q&A Session');
        if (newSession.password) {
          localStorage.setItem('sessionPassword', newSession.password);
        }
      }
      
      // Also authenticate immediately with the password if we have it
      if (newSession && newSession.password) {
        try {
          const authResponse = await api.post('/api/sessions/join', {
            name: newSession.id,
            password: newSession.password
          });
          
          // Update the token with the authenticated token
          const { token } = authResponse.data;
          if (token) {
            localStorage.setItem('sessionToken', token);
            api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          }
        } catch (err) {
          console.error('Error auto-authenticating after session creation:', err);
          // Don't throw here, just log the error
        }
      }
      
      setSession(newSession);
      return newSession;
    } catch (err) {
      console.error('Error creating session:', err);
      setError(err.response?.data?.message || 'Failed to create session');
      throw err;
    }
  };

  // Join an existing session
  const joinSession = async (sessionData) => {
    try {
      setError(null);
      const response = await api.post('/api/sessions/join', sessionData);
      
      // Store the session token
      const { token, session: joinedSession } = response.data;
      if (token) {
        localStorage.setItem('sessionToken', token);
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }
      
      // Store session ID and name in localStorage as fallback
      if (joinedSession && joinedSession.id) {
        localStorage.setItem('sessionId', joinedSession.id);
        localStorage.setItem('sessionName', joinedSession.name || 'Live Q&A Session');
      }
      
      setSession(joinedSession);
      return joinedSession;
    } catch (err) {
      console.error('Error joining session:', err);
      setError(err.response?.data?.message || 'Failed to join session');
      throw err;
    }
  };

  // Authenticate with password for management access
  const authenticateWithPassword = async (sessionId, password) => {
    try {
      setError(null);
      const response = await api.post('/api/sessions/join', {
        name: sessionId,
        password: password
      });
      
      // Store the session token
      const { token, ...sessionData } = response.data;
      if (token) {
        localStorage.setItem('sessionToken', token);
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }
      
      // Store session data in localStorage
      if (sessionData && sessionData.id) {
        localStorage.setItem('sessionId', sessionData.id);
        localStorage.setItem('sessionName', sessionData.name || 'Live Q&A Session');
        localStorage.setItem('sessionPassword', password);
      }
      
      setSession(sessionData);
      return sessionData;
    } catch (err) {
      console.error('Error authenticating with password:', err);
      setError(err.response?.data?.message || 'Invalid password');
      throw err;
    }
  };

  // Leave the current session
  const leaveSession = () => {
    localStorage.removeItem('sessionToken');
    localStorage.removeItem('sessionId');
    localStorage.removeItem('sessionName');
    localStorage.removeItem('sessionPassword');
    delete api.defaults.headers.common['Authorization'];
    setSession(null);
    
    if (socket) {
      socket.disconnect();
      setSocket(null);
    }
  };

  return (
    <SessionContext.Provider
      value={{
        session,
        loading,
        error,
        socket,
        createSession,
        joinSession,
        authenticateWithPassword,
        leaveSession
      }}
    >
      {children}
    </SessionContext.Provider>
  );
}; 